import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { confluenceAPI } from '../services/api';
import colors, { shadows, borderRadius } from '../theme';

function ConfluenceConfigPage() {
    const location = useLocation();

    const [config, setConfig] = useState(null);
    const [status, setStatus] = useState(null);
    const [spaces, setSpaces] = useState([]);
    const [syncLogs, setSyncLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [formData, setFormData] = useState({
        space_keys: [],
        sync_frequency: 'realtime',
        auto_assign: true
    });

    useEffect(() => {
        // Check for OAuth callback parameters
        const params = new URLSearchParams(location.search);
        const oauthSuccess = params.get('success');
        const oauthError = params.get('error');
        const siteName = params.get('site');

        if (oauthSuccess === 'true') {
            setSuccess(`Successfully connected to Confluence workspace: ${decodeURIComponent(siteName || 'Unknown')}`);
            // Clean URL
            window.history.replaceState({}, '', location.pathname);
        } else if (oauthError) {
            setError(`OAuth error: ${oauthError}`);
            window.history.replaceState({}, '', location.pathname);
        }

        loadData();

        // Auto-refresh sync logs every 5 seconds
        const interval = setInterval(async () => {
            try {
                const logsRes = await confluenceAPI.getSyncLogs(10);
                setSyncLogs(logsRes.data.logs);

                // Also refresh status to update running syncs
                const statusRes = await confluenceAPI.getStatus();
                setStatus(statusRes.data.status);
            } catch (err) {
                console.error('Error refreshing sync logs:', err);
            }
        }, 5000); // Refresh every 5 seconds

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [configRes, statusRes, logsRes] = await Promise.all([
                confluenceAPI.getConfig(),
                confluenceAPI.getStatus(),
                confluenceAPI.getSyncLogs(10)
            ]);

            if (configRes.data.configured) {
                setConfig(configRes.data.config);
                setFormData({
                    space_keys: configRes.data.config.space_keys || [],
                    sync_frequency: configRes.data.config.sync_frequency || 'realtime',
                    auto_assign: configRes.data.config.auto_assign !== false
                });

                // Load available spaces
                try {
                    const spacesRes = await confluenceAPI.getSpaces();
                    setSpaces(spacesRes.data.spaces || []);
                } catch (err) {
                    console.error('Error loading spaces:', err);
                }
            }

            setStatus(statusRes.data.status);
            setSyncLogs(logsRes.data.logs);
        } catch (err) {
            console.error('Error loading Confluence data:', err);
            setError('Failed to load Confluence configuration');
        } finally {
            setLoading(false);
        }
    };

    const handleConnectConfluence = async () => {
        setError('');
        try {
            const response = await confluenceAPI.getAuthUrl();
            if (response.data.success && response.data.authUrl) {
                // Redirect to Atlassian OAuth
                window.location.href = response.data.authUrl;
            } else {
                setError('Failed to generate authorization URL');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to start OAuth flow');
        }
    };

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);

        try {
            const payload = {
                space_keys: formData.space_keys,
                sync_frequency: formData.sync_frequency,
                auto_assign: formData.auto_assign
            };

            const response = await confluenceAPI.updateConfig(payload);

            if (response.data.success) {
                setSuccess('Confluence configuration updated successfully!');
                loadData(); // Reload to get updated config
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTriggerSync = async () => {
        setError('');
        setSuccess('');
        setSyncing(true);

        try {
            await confluenceAPI.sync();
            setSuccess('Sync started! Check the sync logs below for progress.');

            // Refresh logs after a delay
            setTimeout(() => {
                loadData();
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to trigger sync');
        } finally {
            setSyncing(false);
        }
    };

    const handleDeleteConfig = async () => {
        if (!window.confirm('Are you sure you want to disconnect Confluence? This will not delete synced knowledge entries.')) {
            return;
        }

        try {
            await confluenceAPI.deleteConfig();
            setSuccess('Confluence integration disconnected successfully');
            setConfig(null);
            setSpaces([]);
            setFormData({
                space_keys: [],
                sync_frequency: 'realtime',
                auto_assign: true
            });
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete configuration');
        }
    };

    const handleSpaceToggle = (spaceId) => {
        setFormData(prev => {
            const newSpaceKeys = prev.space_keys.includes(spaceId)
                ? prev.space_keys.filter(k => k !== spaceId)
                : [...prev.space_keys, spaceId];
            return { ...prev, space_keys: newSpaceKeys };
        });
    };

    if (loading) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Loading Confluence configuration...</p>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: colors.gray[50], padding: '2rem' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '2rem' }}>
                    <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                        Confluence Integration
                    </h1>
                    <p style={{ color: colors.gray[600] }}>
                        Connect your Atlassian Confluence workspace to automatically sync pages and generate AI-powered quizzes
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div style={{
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: colors.red[50],
                        color: colors.red[600],
                        borderRadius: borderRadius.md,
                        border: `1px solid ${colors.red[600]}`
                    }}>
                        {error}
                    </div>
                )}

                {success && (
                    <div style={{
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: colors.green[100],
                        color: colors.green[700],
                        borderRadius: borderRadius.md,
                        border: `1px solid ${colors.green[500]}`
                    }}>
                        {success}
                    </div>
                )}

                {/* OAuth Connection Card (if not configured) */}
                {!config && (
                    <div style={{
                        backgroundColor: colors.white,
                        padding: '2rem',
                        borderRadius: borderRadius.lg,
                        boxShadow: shadows.sm,
                        marginBottom: '2rem',
                        textAlign: 'center'
                    }}>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                            Connect to Confluence
                        </h2>
                        <p style={{ color: colors.gray[600], marginBottom: '2rem' }}>
                            Click below to authorize Miblio to access your Confluence workspace
                        </p>
                        <button
                            onClick={handleConnectConfluence}
                            style={{
                                padding: '1rem 2rem',
                                backgroundColor: colors.blue[500],
                                color: colors.white,
                                border: 'none',
                                borderRadius: borderRadius.md,
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Connect to Confluence
                        </button>
                        <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: colors.gray[500] }}>
                            <p>You'll be redirected to Atlassian to authorize access</p>
                            <p>Required permissions: Read content and spaces</p>
                        </div>
                    </div>
                )}

                {/* Status Card */}
                {status && status.configured && (
                    <div style={{
                        backgroundColor: colors.white,
                        padding: '1.5rem',
                        borderRadius: borderRadius.lg,
                        boxShadow: shadows.sm,
                        marginBottom: '2rem'
                    }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                            Integration Status
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                    Status
                                </p>
                                <p style={{ fontSize: '1.25rem', fontWeight: '600', color: status.active ? colors.green[500] : colors.gray[400] }}>
                                    {status.active ? '✅ Active' : '⏸️ Inactive'}
                                </p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                    Workspace
                                </p>
                                <p style={{ fontSize: '1rem', fontWeight: '600' }}>
                                    {status.siteUrl || 'N/A'}
                                </p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                    Synced Entries
                                </p>
                                <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    {status.syncedKnowledgeCount}
                                </p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                    Sync Method
                                </p>
                                <p style={{ fontSize: '1rem', fontWeight: '600' }}>
                                    {status.syncFrequency === 'realtime' ? '🔄 Real-time' :
                                     status.syncFrequency === 'hourly' ? '⏰ Hourly' :
                                     '🖐️ Manual'}
                                </p>
                            </div>
                            {status.latestSync && (
                                <div>
                                    <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                        Last Sync
                                    </p>
                                    <p style={{ fontSize: '1rem', fontWeight: '600' }}>
                                        {new Date(status.latestSync.sync_started_at).toLocaleString()}
                                    </p>
                                    <p style={{ fontSize: '0.75rem', color: colors.gray[500] }}>
                                        {status.latestSync.status === 'completed' ? '✅' : '❌'} {status.latestSync.pages_processed} pages
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Synced Spaces */}
                        {status.spaceKeys && status.spaceKeys.length > 0 && (
                            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.gray[200]}` }}>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.5rem' }}>
                                    Syncing Spaces:
                                </p>
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    {status.spaceKeys.map(key => (
                                        <span
                                            key={key}
                                            style={{
                                                padding: '0.25rem 0.75rem',
                                                backgroundColor: colors.blue[100],
                                                color: colors.blue[700],
                                                borderRadius: borderRadius.sm,
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {key}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Configuration Form */}
                {config && (
                    <div style={{
                        backgroundColor: colors.white,
                        padding: '2rem',
                        borderRadius: borderRadius.lg,
                        boxShadow: shadows.sm,
                        marginBottom: '2rem'
                    }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                            Configuration
                        </h2>

                        <form onSubmit={handleSaveConfig}>
                            {/* Space Selection */}
                            {spaces.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        marginBottom: '0.5rem',
                                        color: colors.gray[700]
                                    }}>
                                        Spaces to Sync
                                    </label>
                                    <div style={{
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        border: `1px solid ${colors.gray[300]}`,
                                        borderRadius: borderRadius.md,
                                        padding: '0.5rem'
                                    }}>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0.5rem',
                                            cursor: 'pointer',
                                            borderRadius: borderRadius.sm
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.space_keys.length === 0}
                                                onChange={() => setFormData({ ...formData, space_keys: [] })}
                                                style={{ marginRight: '0.5rem' }}
                                            />
                                            <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                                                All Spaces
                                            </span>
                                        </label>
                                        {spaces.map(space => (
                                            <label
                                                key={space.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '0.5rem',
                                                    cursor: 'pointer',
                                                    borderRadius: borderRadius.sm,
                                                    backgroundColor: formData.space_keys.includes(space.id) ? colors.blue[50] : 'transparent'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={formData.space_keys.includes(space.id)}
                                                    onChange={() => handleSpaceToggle(space.id)}
                                                    style={{ marginRight: '0.5rem' }}
                                                />
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                        {space.name}
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: colors.gray[500] }}>
                                                        {space.key} • {space.type}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    <p style={{ fontSize: '0.75rem', color: colors.gray[600], marginTop: '0.25rem' }}>
                                        Select specific spaces or leave all unchecked to sync all spaces
                                    </p>
                                </div>
                            )}

                            {/* Sync Frequency */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    marginBottom: '0.5rem',
                                    color: colors.gray[700]
                                }}>
                                    Sync Method
                                </label>
                                <select
                                    value={formData.sync_frequency}
                                    onChange={(e) => setFormData({ ...formData, sync_frequency: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `1px solid ${colors.gray[300]}`,
                                        borderRadius: borderRadius.md,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    <option value="realtime">Real-time (Webhooks + Polling)</option>
                                    <option value="hourly">Hourly (Polling Only)</option>
                                    <option value="manual">Manual Only</option>
                                </select>
                                <p style={{ fontSize: '0.75rem', color: colors.gray[600], marginTop: '0.25rem' }}>
                                    {formData.sync_frequency === 'realtime' && 'Syncs immediately when pages change in Confluence'}
                                    {formData.sync_frequency === 'hourly' && 'Checks for changes every hour'}
                                    {formData.sync_frequency === 'manual' && 'Only syncs when you manually trigger it'}
                                </p>
                            </div>

                            {/* Auto Assign */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.auto_assign}
                                        onChange={(e) => setFormData({ ...formData, auto_assign: e.target.checked })}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    <span style={{ fontSize: '0.875rem', fontWeight: '500', color: colors.gray[700] }}>
                                        Auto-assign synced content to all users
                                    </span>
                                </label>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: saving ? colors.gray[400] : colors.blue[500],
                                        color: colors.white,
                                        border: 'none',
                                        borderRadius: borderRadius.md,
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: saving ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {saving ? 'Saving...' : 'Save Configuration'}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleTriggerSync}
                                    disabled={syncing}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: syncing ? colors.gray[400] : colors.green[500],
                                        color: colors.white,
                                        border: 'none',
                                        borderRadius: borderRadius.md,
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: syncing ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {syncing ? 'Syncing...' : 'Trigger Sync Now'}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleDeleteConfig}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: colors.white,
                                        color: colors.red[600],
                                        border: `1px solid ${colors.red[600]}`,
                                        borderRadius: borderRadius.md,
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        marginLeft: 'auto'
                                    }}
                                >
                                    Disconnect Confluence
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Sync Logs */}
                {syncLogs.length > 0 && (
                    <div style={{
                        backgroundColor: colors.white,
                        padding: '2rem',
                        borderRadius: borderRadius.lg,
                        boxShadow: shadows.sm
                    }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                            Recent Sync Logs
                        </h2>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: `2px solid ${colors.gray[200]}` }}>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Started</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Status</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Triggered By</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Processed</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Created</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Updated</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Failed</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {syncLogs.map((log) => (
                                        <tr key={log.id} style={{ borderBottom: `1px solid ${colors.gray[200]}` }}>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                                                {new Date(log.sync_started_at).toLocaleString()}
                                            </td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: borderRadius.sm,
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    backgroundColor: log.status === 'completed' ? colors.green[100] : log.status === 'running' ? colors.blue[100] : colors.red[50],
                                                    color: log.status === 'completed' ? colors.green[700] : log.status === 'running' ? colors.blue[700] : colors.red[600]
                                                }}>
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                                                {log.triggered_by === 'webhook' && '🔔 Webhook'}
                                                {log.triggered_by === 'polling' && '🔄 Polling'}
                                                {log.triggered_by === 'manual' && '🖐️ Manual'}
                                                {log.triggered_by === 'cron' && '⏰ Scheduled'}
                                            </td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{log.pages_processed || 0}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.green[600] }}>{log.pages_created || 0}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.blue[600] }}>{log.pages_updated || 0}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.red[600] }}>{log.pages_failed || 0}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{log.duration_seconds || 0}s</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ConfluenceConfigPage;
